// Data-science classification utilities for the Spends Log analytics dashboard.
// Pure functions — no side effects. Keep exec-friendly, well-labelled buckets.

// The eight verticals leadership wants on the "Spends by Vertical" graph (fixed order).
export const VERTICAL_GRAPH_ORDER = ["ATL", "BTL", "Brand", "Digital", "Admin", "Technology", "CX", "CXX"];

// Agency channel mix — the order shown to directors/chairman.
export const AGENCY_CHANNEL_ORDER = [
  "Vertical Intent", "Outdoor", "Portals", "Native Ads", "Asset Creation", "Events & Launch", "Other",
];

// A short human explanation of what each Agency channel captures (for tooltips).
export const AGENCY_CHANNEL_HINT = {
  "Vertical Intent": "Intent-led digital: performance ads, social, programmatic",
  "Outdoor": "OOH: hoardings, airport, unipoles, site branding",
  "Portals": "Real-estate listing portals: 99acres, Housing, Magicbricks, NoBroker",
  "Native Ads": "Native networks: Taboola, Outbrain, Cereone",
  "Asset Creation": "Creatives, print, walkthroughs, brochures, CEC branding",
  "Events & Launch": "Clubhouse launches, events, sponsorships, activations",
  "Other": "PR, subscriptions, tooling & misc agency services",
};

/**
 * Classify an Agency spend line into a marketing channel using its activity /
 * sub-line-item text. Order of checks matters (most specific first).
 */
export function agencyChannel(text) {
  const a = String(text || "").toLowerCase();
  if (!a) return "Other";
  if (/(outdoor|hoarding|airport|unipole|billboard|wayboard|display|site branding|wall\s*wrap|signage|gantry)/.test(a)) return "Outdoor";
  if (/(portal|99\s*acres|99acres|housing|magicbricks|magic\s*bricks|nobroker|no\s*broker|commonfloor|proptiger|sulekha|quikr|olx|squareyards|square\s*yards)/.test(a)) return "Portals";
  if (/(native|taboola|outbrain|cereone|colombia|dable)/.test(a)) return "Native Ads";
  if (/(performance|social media|social\b|meta|facebook|instagram|google|youtube|linkedin|programmatic|surrogate|ppc|sem|intent|search ads|display ads)/.test(a)) return "Vertical Intent";
  if (/(assest|asset|creative|print|walkthrough|collateral|brochure|cec|branding|mounting|design|shoot|photograph|video|3d)/.test(a)) return "Asset Creation";
  if (/(clubhouse|launch|event|sponsor|activation|license|licence|royalty|interstate|appearance|apperance|consultancy|hospitality)/.test(a)) return "Events & Launch";
  return "Other";
}

/**
 * Resolve the effective Agency channel for a spend row. A sticky manual
 * override (`row.agency_channel`, persisted in the DB) always wins over the
 * keyword auto-classification.
 */
export function resolveAgencyChannel(row) {
  const ov = row && row.agency_channel;
  if (ov && AGENCY_CHANNEL_ORDER.includes(ov)) return ov;
  return agencyChannel((row && (row.activity || row.sub_line_item)) || "");
}

/** Is this row's channel driven by a manual override (not auto-classification)? */
export function isChannelOverridden(row) {
  const ov = row && row.agency_channel;
  return !!(ov && AGENCY_CHANNEL_ORDER.includes(ov));
}

// ---------------------------------------------------------------------------
// PERIOD helpers (Period Compare) — mirror the backend fiscal calendar.
// ---------------------------------------------------------------------------
export const MONTH_ORDER = [
  "Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26",
  "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27",
];
export const QUARTER_ORDER = ["Q1", "Q2", "Q3", "Q4"];
export const MONTH_QUARTER = {
  "Apr-26": "Q1", "May-26": "Q1", "Jun-26": "Q1",
  "Jul-26": "Q2", "Aug-26": "Q2", "Sep-26": "Q2",
  "Oct-26": "Q3", "Nov-26": "Q3", "Dec-26": "Q3",
  "Jan-27": "Q4", "Feb-27": "Q4", "Mar-27": "Q4",
};

/** The period bucket a row belongs to for a given granularity. */
export function periodKey(row, granularity) {
  if (granularity === "quarter") {
    return row.quarter || MONTH_QUARTER[row.spend_month] || "";
  }
  return row.spend_month || "";
}

/** Distinct periods present in `rows`, sorted in fiscal order. */
export function orderedPeriods(rows, granularity) {
  const order = granularity === "quarter" ? QUARTER_ORDER : MONTH_ORDER;
  const present = new Set();
  for (const r of rows || []) {
    const k = periodKey(r, granularity);
    if (k) present.add(k);
  }
  return order.filter((p) => present.has(p));
}

/** Sum spend rows into {name, spend, count} groups keyed by keyFn(row). */
export function groupSum(rows, keyFn) {
  const m = new Map();
  for (const r of rows || []) {
    const k = keyFn(r);
    const name = k === undefined || k === null || k === "" ? "\u2014" : k;
    const cur = m.get(name) || { name, spend: 0, count: 0 };
    cur.spend += Number(r.amount || 0);
    cur.count += 1;
    m.set(name, cur);
  }
  return Array.from(m.values());
}

/** Sort desc by spend and collapse the long tail into a single "Others" bucket. */
export function topNWithOthers(items, n = 12) {
  const sorted = [...(items || [])].sort((a, b) => b.spend - a.spend);
  if (sorted.length <= n) return sorted;
  const head = sorted.slice(0, n);
  const tail = sorted.slice(n);
  const others = tail.reduce((acc, x) => ({ name: "Others", spend: acc.spend + x.spend, count: acc.count + x.count }), { name: "Others", spend: 0, count: 0 });
  return [...head, others];
}

/** Order a grouped list by an explicit label order, dropping labels not in the order. */
export function orderBy(items, order) {
  const map = Object.fromEntries((items || []).map((x) => [x.name, x]));
  return order.map((name) => map[name] || { name, spend: 0, count: 0 });
}
